import React, { useEffect, useState } from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CLIENT_ACTIVITIES_ROUTE, NOTIFICATION_ROUTE } from '../../utils/routes';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { SUBSCRIBE_TO_COACH_WEB_NOTIFICATIONS } from '../../service/graphql/notificationQuery';
import scrollbarStyles from '../../utils/styles';
import {
  INotification,
  getNotificationsRequest,
  coachWebNotificationsReceived,
  ICoachWebNotificationPayload,
  viewWebNotifications,
  WebNotificationType,
} from '../../redux/notification/notificationSlice';
import NotificationMessageCard from './NotificationMessageCard';
import { generateClientActivityStatusSummeryRequest } from '../../redux/client/clientSlice';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

// red dot if there is a new notification
interface INotificationProps {
  onClick: () => void;
}

const Notification = ({ onClick }: INotificationProps) => {
  const { t, i18n } = useTranslation();
  const appLanguage = i18n.language;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const notifications = useAppSelector((state) => state.notification.notifications);
  const notViewedNotifications = notifications.filter((notification) => !notification.isViewed);
  const coach = useAppSelector((state) => state.coach.coach);
  const coachId = coach?.id || '';
  const timeZone = useAppSelector((state) => state.coach.coach?.timeZone);
  useEffect(() => {
    dispatch(
      getNotificationsRequest({
        coachId,
        skip: 0,
        take: 10,
        searchQuery: '',
        isViewed: undefined,
      })
    );
  }, [dispatch, coachId, notifications]);

  type IGroupedNotifications = { [date: string]: INotification[] };
  const groupedNotifications = notifications.reduce((acc, notification) => {
    const date = dayjs(notification.createdAt).format('YYYY-MM-DD');
    const updatedAcc = { ...acc };
    updatedAcc[date] = [...(updatedAcc[date] || []), notification];
    return updatedAcc;
  }, {} as IGroupedNotifications);

  const { data } = useSubscription<ICoachWebNotificationPayload>(
    SUBSCRIBE_TO_COACH_WEB_NOTIFICATIONS,
    {
      variables: { coachId },
      skip: !coachId,
    }
  );

  const handleSendMail = () => {
    if (coach?.id) {
      dispatch(
        generateClientActivityStatusSummeryRequest({
          coachId: coach.id,
          timeZone,
        })
      );
    }
  };

  useEffect(() => {
    if (data) {
      dispatch(coachWebNotificationsReceived(data as INotification));
    }
  }, [data, dispatch]);

  const handleBellClick = () => {
    if (onClick) {
      onClick();
    }
    setIsDrawerOpen(!isDrawerOpen);

    if (notViewedNotifications.length > 0) {
      dispatch(
        viewWebNotifications({
          coachId,
        })
      );
    }
  };
  const handleNavigation = () => {
    setIsDrawerOpen(false);
    navigate(NOTIFICATION_ROUTE);
  };

  const handleActivityNavigation = (id: string) => {
    navigate(
      CLIENT_ACTIVITIES_ROUTE.replace(':id', id)
        .replace('/:groupid?', '')
        .replace('/:groupname?', '')
    );
  };

  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  return (
    <Box sx={{ position: 'relative' }}>
      <img
        src="/assets/icons/bell.svg"
        alt="Bell"
        onClick={handleBellClick}
        style={{ cursor: 'pointer' }}
        role="presentation"
      />
      {notViewedNotifications.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '-2px',
            right: '-1px',
            background: 'linear-gradient(175.46deg, #FF9A9A -9.81%, #FF5858 96.32%)',
            width: '15px',
            height: '15px',
            borderRadius: '50%',
            padding: '4px',
            fontSize: '8px',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {notViewedNotifications.length}
        </Box>
      )}
      {/* Drawer component */}
      <Drawer
        anchor={appLanguage === 'ar' ? 'left' : 'right'}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: '20px',
            borderBottomLeftRadius: '20px',
            padding: '16px',
          },
        }}
      >
        {/* Content of the drawer */}
        <Box role="presentation" width={450} display="flex" flexDirection="column" height="100%">
          {/* Add Notification List here */}
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6">{t('notifications.resentNotifications')}</Typography>
            <Box display="flex" justifyContent="space-between" gap="12px" alignItems="center">
              <Typography
                variant="body1"
                fontSize={13}
                fontWeight={700}
                onClick={handleNavigation}
                sx={{
                  backgroundImage: 'linear-gradient(137.65deg, #6599A5 24.11%, #3B667E 83.2%)',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  cursor: 'pointer',
                  borderBottom: '2px solid transparent',
                  display: 'inline-block',
                  textDecoration: 'underline',
                  borderBottomColor: '#6599A5',
                }}
              >
                {t('notifications.viewAll')}
              </Typography>
              <img
                src="/assets/icons/close-circle-large.svg"
                alt="close"
                onClick={() => setIsDrawerOpen(false)}
                role="presentation"
                style={{ cursor: 'pointer', width: '24px', height: '24px' }}
              />
            </Box>
          </Box>
          <Box py="16px" flex="1" overflow="auto" sx={scrollbarStyles} pr="10px">
            {Object.entries(groupedNotifications).length === 0 ? (
              <Typography variant="h6" color="textSecondary" textAlign="center">
                {t('notifications.noNotifications')}
              </Typography>
            ) : (
              Object.entries(groupedNotifications).map(([groupedDate, latestNotifications]) => {
                const formattedDate = (date: string): string => {
                  const today = dayjs();
                  const yesterday = today.subtract(1, 'day');

                  // Check for "today" first for potential performance optimization
                  if (dayjs(date).isSame(today, 'day')) {
                    return t('notifications.today');
                  }
                  if (dayjs(date).isSame(yesterday, 'day')) {
                    return t('notifications.yesterday');
                  }
                  return dayjs(date).format('D MMMM YYYY');
                };
                return (
                  <Box key={groupedDate} display="flex" flexDirection="column" gap="20px">
                    <Typography
                      variant="meta"
                      color="textSecondary"
                      fontSize={16}
                      fontWeight={500}
                      textAlign={appLanguage === 'ar' ? 'right' : 'left'}
                    >
                      {formattedDate(groupedDate)}
                    </Typography>
                    {latestNotifications.map((notification, index) =>
                      notification ? (
                        <NotificationMessageCard
                          key={notification.id}
                          index={index}
                          totalItems={latestNotifications.length}
                          message={{
                            status: notification.status,
                            title: notification.title,
                            message: notification.description,
                            time: notification.createdAt,
                            profilePic: notification.client?.profilePic,
                          }}
                          onClick={() =>
                            notification.type === WebNotificationType.ACTIVITY
                              ? handleActivityNavigation(notification.client?.id)
                              : {}
                          }
                        />
                      ) : null
                    )}
                  </Box>
                );
              })
            )}
          </Box>
          <Box>
            {checkPermissions(UserPermissionType.CORE_MODULE_SEND_ACTIVITY_SUMMARY) && (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="body1"
                  fontWeight={700}
                  fontSize="17px"
                  sx={{
                    backgroundImage: 'linear-gradient(137.65deg, #6599A5 24.11%, #3B667E 83.2%)',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  {t('notifications.emailMe')}
                </Typography>

                <IconButton
                  onClick={handleSendMail}
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1)',
                    },
                  }}
                >
                  <img src="/assets/icons/send-mail.svg" alt="right-arrow" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Notification;
